import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { Eyebrow, MaskLines, ClipImage, Reveal, EASE } from "./Motion";
import { YouTubeLayer } from "./YouTubeLayer";

const ClipYouTube = ({ id, poster, label, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.25 });
  const [failed, setFailed] = useState(false);
  const onError = useCallback(() => setFailed(true), []);
  return (
    <motion.div ref={ref} className={className} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}>
      <motion.div className="relative h-full w-full overflow-hidden bg-[#121418]" variants={{ hidden: { clipPath: "inset(100% 0% 0% 0%)" }, show: { clipPath: "inset(0% 0% 0% 0%)" } }} transition={{ duration: 1.4, ease: EASE }}>
        <img src={poster} alt={label} className="absolute inset-0 h-full w-full object-cover" />
        {!failed && <YouTubeLayer id={id} playing={inView} onError={onError} testid={`news-youtube-${id}`} />}
        {failed && (
          <a href={`https://www.youtube.com/watch?v=${id}`} target="_blank" rel="noreferrer" data-testid={`news-youtube-link-${id}`} className="link-u absolute bottom-5 left-5 pb-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#f5f3ef]">
            Watch on YouTube
          </a>
        )}
      </motion.div>
    </motion.div>
  );
};

const ClipVideo = ({ src, poster, label, className }) => (
  <motion.div className={className} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}>
    <motion.div className="relative h-full w-full overflow-hidden" variants={{ hidden: { clipPath: "inset(100% 0% 0% 0%)" }, show: { clipPath: "inset(0% 0% 0% 0%)" } }} transition={{ duration: 1.4, ease: EASE }}>
      <video data-testid="news-video-becky-g" aria-label={label} src={src} poster={poster} autoPlay muted loop playsInline preload="metadata" className="h-full w-full object-cover" />
    </motion.div>
  </motion.div>
);

const POSTS = [
  {
    id: "joop-jeans-aw26",
    tag: "JOOP! Jeans · AW26",
    title: ["Joop Jeans", "Autumn / Winter 26."],
    body: "The JOOP! Jeans Fall / Winter 2026 campaign movie. See the new Joop Jeans collection for men and women in person at our Dundrum showroom.",
    img: "/assets/reels/joop-jeans-aw26.jpg",
    youtube: "c9Ycp7Rpg1Q",
    aspect: "aspect-[4/3]",
    slug: "joop",
  },
  {
    id: "gstar-arc",
    tag: "G-Star · Anatomic Denim",
    title: ["The new Arc."],
    body: "The new Arc pushes the original concept further. Bolder proportions and an unmistakable O-shape: arched legs, low waist, relaxed rise. Engineered panels along the inseam, with twisted inseams and outseams that rotate around the leg.",
    img: "/assets/gstar-elwood.jpg",
    slug: "g-star",
  },
  {
    id: "guess-b-jean",
    tag: "Guess · with Becky G",
    title: ["Introducing", "The B Jean."],
    body: "Reviving the iconic early-2000s denim fits. Five new styles, one low-rise signature. From sleek bootcut to bold flare — discover fresh washes and distinctive detail.",
    img: "/assets/reels/becky-g-guess.jpg",
    video: "/assets/reels/becky-g-guess.mp4",
    slug: "guess-clothing",
  },
];

const Post = ({ p, flip }) => (
  <article data-testid={`news-${p.id}`} className="grid grid-cols-1 items-center gap-10 py-16 lg:grid-cols-12 lg:gap-16">
    {p.youtube ? (
      <ClipYouTube id={p.youtube} poster={p.img} label={p.tag} className={`${p.aspect || "aspect-video"} w-full lg:col-span-7 ${flip ? "lg:order-2 lg:col-start-6" : ""}`} />
    ) : p.video ? (
      <ClipVideo src={p.video} poster={p.img} label={p.tag} className={`aspect-video w-full lg:col-span-7 ${flip ? "lg:order-2 lg:col-start-6" : ""}`} />
    ) : (
      <ClipImage src={p.img} alt={p.tag} className={`aspect-[4/5] w-full lg:col-span-5 ${flip ? "lg:order-2 lg:col-start-8" : ""}`} />
    )}
    <div className={`${p.video || p.youtube ? "lg:col-span-4" : "lg:col-span-6"} ${flip ? "lg:order-1 lg:col-start-1" : p.video || p.youtube ? "lg:col-start-9" : "lg:col-start-7"}`}>
      <Reveal><span className="font-mono text-xs uppercase tracking-[0.25em] text-[#c8a97e]">{p.tag}</span></Reveal>
      <h3 className="mt-6 font-display text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">
        <MaskLines inView lines={p.title} />
      </h3>
      <Reveal delay={0.1}><p className="mt-8 max-w-md text-base leading-relaxed text-[#c9c6c0]">{p.body}</p></Reveal>
      <Reveal delay={0.2}>
        <Link to={`/brands/${p.slug}`} data-testid={`news-link-${p.id}`} className="group mt-10 inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.16em] text-[#f5f3ef]">
          <span className="link-u pb-1">Discover the brand</span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
      </Reveal>
    </div>
  </article>
);

export const News = () => (
  <section id="news" data-testid="news-section" className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10 sm:py-32 lg:px-14">
    <Eyebrow index="04">News & Campaigns</Eyebrow>
    {POSTS.map((p, i) => <Post key={p.id} p={p} flip={i % 2 === 1} />)}
  </section>
);
